import React, { useEffect, createElement } from 'react';
import { IMAGES } from '../constants/images';
import { ORDER_URL } from '../constants/translations';
import { useLanguage } from '../context/LanguageContext';
const SITE_URL = 'https://tartahtx.com';
const OG_IMAGE = IMAGES.openBox;
/** Upsert a <meta> tag by name or property. */
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
/** Upsert a <link rel> tag. */
function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
/** Upsert a JSON-LD structured-data block by id. */
function setJsonLd(id: string, data: unknown) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
/**
 * Manages all document <head> SEO metadata (title, description, canonical,
 * Open Graph, Twitter cards, hreflang, and JSON-LD structured data).
 * Renders nothing; reacts to the active language.
 */
export function Seo() {
  const {
    t,
    lang
  } = useLanguage();
  const s = t.seo;
  useEffect(() => {
    // Language + title
    document.documentElement.lang = lang;
    document.title = s.title;
    // Primary meta
    setMeta('name', 'description', s.description);
    setMeta('name', 'keywords', s.keywords);
    setMeta('name', 'author', 'TARTA');
    setMeta('name', 'robots', 'index, follow');
    setMeta('name', 'viewport', 'width=device-width, initial-scale=1, viewport-fit=cover');
    setMeta('name', 'theme-color', '#241C18');
    // Canonical + hreflang
    setLink('canonical', SITE_URL);
    // Open Graph
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'TARTA');
    setMeta('property', 'og:title', s.title);
    setMeta('property', 'og:description', s.description);
    setMeta('property', 'og:url', SITE_URL);
    setMeta('property', 'og:image', OG_IMAGE);
    setMeta('property', 'og:image:alt', s.ogAlt);
    setMeta('property', 'og:locale', lang === 'es' ? 'es_US' : 'en_US');
    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', s.title);
    setMeta('name', 'twitter:description', s.description);
    setMeta('name', 'twitter:image', OG_IMAGE);
    setMeta('name', 'twitter:site', '@tartahtx');
    // Structured data — LocalBusiness / Bakery
    setJsonLd('ld-business', {
      '@context': 'https://schema.org',
      '@type': 'Bakery',
      name: 'TARTA',
      description: s.description,
      image: OG_IMAGE,
      url: SITE_URL,
      email: 'tarta@tartahtx.com',
      servesCuisine: 'Basque cheesecake',
      priceRange: '$$',
      areaServed: 'Houston, TX',
      sameAs: ['https://instagram.com/tartahtx'],
      address: {
        '@type': 'PostalAddress',
        streetAddress: '10840 Beinhorn Rd',
        addressLocality: 'Houston',
        addressRegion: 'TX',
        postalCode: '77024',
        addressCountry: 'US'
      },
      location: {
        '@type': 'Place',
        name: 'Memorial Villages Farmers Market',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '10840 Beinhorn Rd',
          addressLocality: 'Houston',
          addressRegion: 'TX',
          postalCode: '77024',
          addressCountry: 'US'
        }
      },
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '13:00'
      }],
      potentialAction: {
        '@type': 'OrderAction',
        target: ORDER_URL
      },
      hasMenu: {
        '@type': 'Menu',
        hasMenuSection: {
          '@type': 'MenuSection',
          name: 'Basque Cheesecakes',
          hasMenuItem: t.flavors.list.map(f => ({
            '@type': 'MenuItem',
            name: f.name,
            description: f.copy
          }))
        }
      }
    });
    // Structured data — FAQPage
    setJsonLd('ld-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: t.faq.items.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a
        }
      }))
    });
  }, [lang, s, t]);
  return null;
}